import { useCallback, useMemo } from 'react';
import { CarlineWithOfertas, Oferta } from '../services/ofertas-service/models/ofertas';
import { useTrackingManager } from '../tracking/use-tracking-manager';
import { getPagoFinanciado, getPagoUnico } from './ofertas-helper';

const getPayload = (carline?: CarlineWithOfertas, oferta?: Oferta) => {
	const carlineOk = carline || oferta?.carline;
	const ofertaOk = oferta || carline?.ofertas[0];

	if (!carlineOk || !ofertaOk) return undefined;

	const pagoFinanciado = getPagoFinanciado(ofertaOk.datosEconomicos);
	const pagoUnico = getPagoUnico(ofertaOk.datosEconomicos);

	return {
		CarlineId: carlineOk.code.toString(),
		CarlineName: carlineOk.title,
		ModelId: ofertaOk.configuracion.motorizacion.code?.split('-')[0],
		ModelName: ofertaOk.carline?.salesgroup.model.name,
		ModelYear: ofertaOk.carline?.salesgroup.year.toString(),
		SalesgroupName: ofertaOk.carline?.salesgroup.title,
		PriceRateType: pagoFinanciado?.financiacion?.tipo ?? undefined,
		PriceRateTotal: pagoFinanciado?.precio ?? undefined,
		PriceRateCurrency: 'EUR',
		PriceTotalTotal: pagoUnico?.precio ?? undefined,
		PriceTotalCurrency: 'EUR',
	};
};

export const useOfertasTracker = () => {
	const trackingManager = useTrackingManager();

	// useCallback con dependencia en trackingManager para mantener referencias estables entre renders
	const trackOfertasModelSelectClick = useCallback(
		(carline: CarlineWithOfertas) => {
			const payload = getPayload(carline);
			if (payload) trackingManager.trackOfertasModelSelectClick(payload);
		},
		[trackingManager],
	);

	const trackOfertasVehiclePageLoad = useCallback(
		(carline: CarlineWithOfertas) => {
			const payload = getPayload(carline);
			if (payload) trackingManager.trackOfertasVehiclePageload(payload);
		},
		[trackingManager],
	);

	const trackOfertasVehicleLoad = useCallback(
		(carline: CarlineWithOfertas) => {
			const payload = getPayload(carline);
			if (payload) trackingManager.trackOfertasVehicleLoad(payload);
		},
		[trackingManager],
	);

	const trackOfertasGenericFeatureAppLoad = useCallback(
		(carline: CarlineWithOfertas) => {
			const payload = getPayload(carline);
			if (payload) trackingManager.trackOfertasGenericFeatureAppLoad(payload);
		},
		[trackingManager],
	);

	const trackOfertasButtonClick = useCallback(
		(contentId: string, oferta: Oferta, carline?: CarlineWithOfertas, id?: string) => {
			const payload = getPayload(carline, oferta);
			if (payload) trackingManager.trackOfertasButtonClick(id ? id : contentId, { contentId: contentId }, payload);
		},
		[trackingManager],
	);

	const trackOfertasLayerLoad = useCallback(
		(contentId: string, oferta: Oferta, carline: CarlineWithOfertas) => {
			const payload = getPayload(carline, oferta);
			if (payload) trackingManager.trackOfertasLayerLoad({ contentId: contentId }, payload);
		},
		[trackingManager],
	);

	const trackOfertasLayerClose = useCallback(
		(contentId: string, oferta: Oferta, carline: CarlineWithOfertas, id?: string) => {
			const payload = getPayload(carline, oferta);
			if (payload) trackingManager.trackOfertasLayerClose(id ? id : contentId, { contentId: contentId }, payload);
		},
		[trackingManager],
	);

	const trackOfertasLinkClick = useCallback(
		(contentId: string, oferta: Oferta, carline: CarlineWithOfertas, id?: string) => {
			const payload = getPayload(carline, oferta);
			if (payload) trackingManager.trackOfertasLinkClick(id ? id : contentId, { contentId: contentId }, payload);
		},
		[trackingManager],
	);

	const trackOfertasFilterSelectClick = useCallback(
		(selector: string) => {
			trackingManager.trackOfertasFilterSelectClick({
				type: 'single_value',
				name: 'FT',
				values: [selector],
			});
		},
		[trackingManager],
	);

	const trackOfertasElementSwipe = useCallback(
		(carline: CarlineWithOfertas, oferta?: Oferta) => {
			const payload = getPayload(carline, oferta);
			trackingManager.trackOfertasElementSwipe(payload);
		},
		[trackingManager],
	);

	return useMemo(
		() => ({
			trackOfertasModelSelectClick,
			trackOfertasVehiclePageLoad,
			trackOfertasVehicleLoad,
			trackOfertasGenericFeatureAppLoad,
			trackOfertasButtonClick,
			trackOfertasLayerLoad,
			trackOfertasLayerClose,
			trackOfertasLinkClick,
			trackOfertasFilterSelectClick,
			trackOfertasElementSwipe,
		}),
		[
			trackOfertasModelSelectClick,
			trackOfertasVehiclePageLoad,
			trackOfertasVehicleLoad,
			trackOfertasGenericFeatureAppLoad,
			trackOfertasButtonClick,
			trackOfertasLayerLoad,
			trackOfertasLayerClose,
			trackOfertasLinkClick,
			trackOfertasFilterSelectClick,
			trackOfertasElementSwipe,
		],
	);
};
